import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'shared/ui/dialog'
import { Button } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { Checkbox } from 'shared/ui/checkbox'
import { Search, User, Loader2, XCircle } from 'lucide-react'
import {
  getFieldDatasourceOptions,
  type TemplateField,
  type TemplateFieldDatasourceItem,
  type TemplateFieldDatasourceDisplayColumn,
} from 'shared/api/templateService'
import { cn } from 'shared/utils'

export interface SearchSelectValue {
  value: string
  label: string
}

export type SearchSelectValues = SearchSelectValue[]

interface BaseSearchSelectFieldProps {
  field: TemplateField
  tenantId: string
  dependencyParams?: Record<string, string | undefined>
  dependencyMissing?: boolean
  dependencyLabel?: string
  disabled?: boolean
}

interface SearchSelectFieldProps extends BaseSearchSelectFieldProps {
  value: SearchSelectValue | null
  onChange: (selection: SearchSelectValue | null) => void
}

interface SearchMultiSelectFieldProps extends BaseSearchSelectFieldProps {
  value: SearchSelectValues
  onChange: (selection: SearchSelectValues) => void
}

const DEFAULT_PAGE_SIZE = 10
const EMPTY_DEPENDENCY_PARAMS: Record<string, string | undefined> = {}

const parseDatasourceUiConfig = (rawValue?: string | null): Record<string, unknown> => {
  if (!rawValue) {
    return {}
  }
  try {
    const parsed = JSON.parse(rawValue)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {}
  } catch {
    return {}
  }
}

const resolvePlaceholder = (field: TemplateField): string => {
  const uiConfig = parseDatasourceUiConfig(field.datasource?.uiConfigJson)
  return typeof uiConfig.placeholder === 'string' && uiConfig.placeholder.trim().length > 0
    ? uiConfig.placeholder.trim()
    : 'Search...'
}

const resolveTableColumns = (
  field: TemplateField,
  displayColumns: TemplateFieldDatasourceDisplayColumn[],
): TemplateFieldDatasourceDisplayColumn[] => {
  if (displayColumns.length > 0) {
    return displayColumns
  }

  const fallbackField = field.datasource?.labelColumn ?? 'label'
  const fallbackLabel = field.label || 'Data'

  return [
    {
      key: fallbackField,
      field: fallbackField,
      label: fallbackLabel,
    },
  ]
}

const resolveDatasourceCellValue = (
  field: TemplateField,
  item: TemplateFieldDatasourceItem,
  column: TemplateFieldDatasourceDisplayColumn,
) => {
  const columnKey = column.key ?? column.field
  const normalize = (key: string) => key.trim()
  const normalizeLoose = (key: string) => key.trim().toLowerCase().replace(/[\s_-]+/g, '')
  const normalizedColumn = normalize((columnKey || column.field || '').toString())
  const normalizedFieldName = column.field ? normalize(column.field) : normalizedColumn

  if (item.columns) {
    const directKey = columnKey ?? column.field
    if (directKey && directKey in item.columns) {
      const directValue = item.columns[directKey]
      if (directValue !== undefined && directValue !== null) {
        return directValue ?? ''
      }
    }

    const matchedKey = Object.keys(item.columns).find(
      (key) => normalize(key).localeCompare(normalizedColumn, undefined, { sensitivity: 'accent' }) === 0,
    )

    if (matchedKey) {
      const matchedValue = item.columns[matchedKey]
      if (matchedValue !== undefined && matchedValue !== null) {
        return matchedValue ?? ''
      }
    }

    const looselyMatchedKey = Object.keys(item.columns).find(
      (key) => normalizeLoose(key) === normalizeLoose(normalizedColumn) || normalizeLoose(key) === normalizeLoose(normalizedFieldName),
    )

    if (looselyMatchedKey) {
      const looselyMatchedValue = item.columns[looselyMatchedKey]
      if (looselyMatchedValue !== undefined && looselyMatchedValue !== null) {
        return looselyMatchedValue ?? ''
      }
    }
  }

  if (
    field.datasource?.labelColumn &&
    (normalize(field.datasource.labelColumn).localeCompare(normalizedColumn, undefined, {
      sensitivity: 'accent',
    }) === 0 ||
      normalize(field.datasource.labelColumn).localeCompare(normalizedFieldName, undefined, {
        sensitivity: 'accent',
      }) === 0)
  ) {
    return item.label
  }

  if (
    normalizeLoose(normalizedColumn) === 'label' ||
    normalizeLoose(normalizedColumn) === 'name' ||
    normalizeLoose(normalizedFieldName) === 'label' ||
    normalizeLoose(normalizedFieldName) === 'name'
  ) {
    return item.label
  }

  return ''
}

const buildPreferredSelectionLabel = (
  field: TemplateField,
  item: TemplateFieldDatasourceItem,
  columns: TemplateFieldDatasourceDisplayColumn[],
): string => {
  const isGuidLike = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim())

  const cells = columns
    .map((column) => {
      const cellValue = resolveDatasourceCellValue(field, item, column)
      return cellValue === null || cellValue === undefined ? '' : String(cellValue).trim()
    })
    .filter((value) => value.length > 0 && value !== '-')

  if (cells.length === 0) {
    return item.label ?? item.value
  }

  const normalizedValue = item.value.trim().toLowerCase()
  const deduped = cells.filter((value, index) => cells.indexOf(value) === index)
  const bestDescriptiveCell = deduped.find((cell) => {
    const normalizedCell = cell.trim().toLowerCase()
    if (!normalizedCell) {
      return false
    }
    if (normalizedCell === normalizedValue) {
      return false
    }
    return !isGuidLike(cell)
  })

  if (bestDescriptiveCell) {
    return bestDescriptiveCell
  }

  if (deduped.length === 1) {
    if (deduped[0].trim().toLowerCase() === normalizedValue && item.label && item.label !== item.value) {
      return item.label
    }
    return deduped[0]
  }

  const first = deduped[0]
  const second = deduped[1]
  if (first.trim().toLowerCase() === normalizedValue) {
    return `${first} : ${second}`
  }

  return deduped.join(' : ')
}

const useDatasourceSearch = ({
  field,
  tenantId,
  dependencyParams,
  dependencyMissing,
  selectedValues,
  enabled,
  freezeDependencyWhileOpen = false,
}: {
  field: TemplateField
  tenantId: string
  dependencyParams: Record<string, string | undefined>
  dependencyMissing: boolean
  selectedValues: string[]
  enabled: boolean
  freezeDependencyWhileOpen?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [options, setOptions] = useState<TemplateFieldDatasourceItem[]>([])
  const [displayColumns, setDisplayColumns] = useState<TemplateFieldDatasourceDisplayColumn[]>(
    field.datasource?.displayColumns ?? [],
  )
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastRequestKeyRef = useRef<string | null>(null)
  const selectedValuesRef = useRef<string[]>(selectedValues)
  const fallbackDisplayColumns = useMemo(
    () => field.datasource?.displayColumns ?? [],
    [field.datasource?.displayColumns],
  )

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      setPage(1)
    }, 400)

    return () => window.clearTimeout(handler)
  }, [searchTerm])

  const dependencyKey = useMemo(
    () =>
      JSON.stringify(
        Object.entries(dependencyParams ?? EMPTY_DEPENDENCY_PARAMS)
          .filter(([, value]) => value !== undefined)
          .sort(([left], [right]) => left.localeCompare(right))
          .reduce<Record<string, string | undefined>>((acc, [key, value]) => {
            acc[key] = value
            return acc
          }, {}),
      ),
    [dependencyParams],
  )

  const [effectiveDependencyKey, setEffectiveDependencyKey] = useState(dependencyKey)
  useEffect(() => {
    if (freezeDependencyWhileOpen && isOpen) {
      return
    }
    setEffectiveDependencyKey(dependencyKey)
  }, [dependencyKey, freezeDependencyWhileOpen, isOpen])

  const normalizedDependencyParams = useMemo<Record<string, string | undefined>>(
    () => JSON.parse(effectiveDependencyKey) as Record<string, string | undefined>,
    [effectiveDependencyKey],
  )

  useEffect(() => {
    selectedValuesRef.current = selectedValues.filter((value) => value.trim().length > 0)
  }, [selectedValues])

  useEffect(() => {
    if (freezeDependencyWhileOpen && isOpen) {
      return
    }
    setPage(1)
  }, [effectiveDependencyKey, freezeDependencyWhileOpen, isOpen])

  useEffect(() => {
    setDisplayColumns(field.datasource?.displayColumns ?? [])
  }, [field.datasource])

  const fetchOptions = useCallback(
    async (requestedPage: number, requestedPageSize: number) => {
      const requestKey = JSON.stringify({
        fieldId: field.fieldId,
        dependencyKey: effectiveDependencyKey,
        debouncedSearchTerm,
        page: requestedPage,
        pageSize: requestedPageSize,
      })

      if (lastRequestKeyRef.current === requestKey) {
        return
      }

      lastRequestKeyRef.current = requestKey
      setIsLoading(true)
      setError(null)
      try {
        const response = await getFieldDatasourceOptions(
          field.fieldId,
          { tenantId, tenant: tenantId, ...normalizedDependencyParams },
          debouncedSearchTerm,
          requestedPage,
          requestedPageSize,
          selectedValuesRef.current,
        )

        const items = Array.isArray(response.items) ? response.items : []
        setOptions(items)
        setDisplayColumns((prev) => {
          if (response.displayColumns && response.displayColumns.length > 0) {
            return response.displayColumns
          }
          if (prev.length > 0) {
            return prev
          }
          return fallbackDisplayColumns
        })

        const nextTotalItems =
          typeof response.totalItems === 'number' && response.totalItems >= 0
            ? response.totalItems
            : items.length

        setTotalItems(nextTotalItems)
        setTotalPages(
          response.totalPages && response.totalPages > 0
            ? response.totalPages
            : Math.max(1, Math.ceil(nextTotalItems / requestedPageSize)),
        )
        setPage(response.pageNumber && response.pageNumber > 0 ? response.pageNumber : requestedPage)
        setPageSize(response.pageSize && response.pageSize > 0 ? response.pageSize : requestedPageSize)
      } catch (err) {
        lastRequestKeyRef.current = null
        console.error('Failed to load datasource options', err)
        setError('Unable to load data. Please try again.')
        setOptions([])
        setTotalItems(0)
        setTotalPages(1)
      } finally {
        setIsLoading(false)
      }
    },
    [
      debouncedSearchTerm,
      effectiveDependencyKey,
      field.fieldId,
      fallbackDisplayColumns,
      normalizedDependencyParams,
      tenantId,
    ],
  )

  useEffect(() => {
    if (!enabled || !isOpen || dependencyMissing) {
      return
    }
    fetchOptions(page, pageSize)
  }, [dependencyMissing, enabled, fetchOptions, isOpen, page, pageSize])

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open)
    lastRequestKeyRef.current = null
    if (!open) {
      setSearchTerm('')
      setDebouncedSearchTerm('')
    }
  }, [])

  const tableColumns = useMemo(
    () => resolveTableColumns(field, displayColumns),
    [displayColumns, field],
  )

  const placeholder = useMemo(() => resolvePlaceholder(field), [field])

  const startIndex = useMemo(() => {
    if (totalItems === 0) return 0
    return (page - 1) * pageSize + 1
  }, [page, pageSize, totalItems])

  const endIndex = useMemo(() => {
    if (totalItems === 0) return 0
    return Math.min(page * pageSize, totalItems)
  }, [page, pageSize, totalItems])

  return {
    isOpen,
    setIsOpen: handleOpenChange,
    searchTerm,
    setSearchTerm,
    page,
    setPage,
    pageSize,
    options,
    tableColumns,
    placeholder,
    totalItems,
    totalPages,
    isLoading,
    error,
    startIndex,
    endIndex,
    canGoPrevious: page > 1,
    canGoNext: page < totalPages,
  }
}

export function SearchSelectField({
  field,
  value,
  onChange,
  tenantId,
  dependencyParams = {},
  dependencyMissing = false,
  dependencyLabel,
  disabled = false,
}: SearchSelectFieldProps) {
  const searchState = useDatasourceSearch({
    field,
    tenantId,
    dependencyParams,
    dependencyMissing,
    selectedValues: value?.value ? [value.value] : [],
    enabled: !disabled,
  })
  const labelRequestKeyRef = useRef<string | null>(null)

  const handleSelect = useCallback(
    (item: TemplateFieldDatasourceItem) => {
      const preferredLabel = buildPreferredSelectionLabel(field, item, searchState.tableColumns)
      onChange({ value: item.value, label: preferredLabel })
      searchState.setIsOpen(false)
    },
    [field, onChange, searchState],
  )

  const handleClear = useCallback(() => {
    onChange(null)
    searchState.setIsOpen(false)
  }, [onChange, searchState])

  useEffect(() => {
    if (!value?.value || searchState.options.length === 0) {
      return
    }

    const normalizedSelectedValue = value.value.trim().toLowerCase()
    const matchedOption = searchState.options.find(
      (option) => (option.value ?? '').trim().toLowerCase() === normalizedSelectedValue,
    )
    if (!matchedOption) {
      return
    }

    const preferredLabel = buildPreferredSelectionLabel(field, matchedOption, searchState.tableColumns)
    if (!preferredLabel || preferredLabel === value.label) {
      return
    }

    const currentLabel = value.label?.trim() ?? ''
    if (
      currentLabel.length === 0 ||
      currentLabel.toLowerCase() === normalizedSelectedValue ||
      currentLabel.length < preferredLabel.length
    ) {
      onChange({
        value: value.value,
        label: preferredLabel,
      })
    }
  }, [field, onChange, searchState.options, searchState.tableColumns, value])

  useEffect(() => {
    if (dependencyMissing) {
      return
    }

    const selectedValue = value?.value?.trim() ?? ''
    if (!selectedValue) {
      return
    }

    const currentLabel = value?.label?.trim() ?? ''
    const isGuidLike = (candidate: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidate)
    const looksUnresolved =
      currentLabel.length === 0 ||
      currentLabel.toLowerCase() === selectedValue.toLowerCase() ||
      isGuidLike(currentLabel)
    if (!looksUnresolved) {
      return
    }

    const requestKey = JSON.stringify({
      fieldId: field.fieldId,
      selectedValue: selectedValue.toLowerCase(),
      dependencyParams: Object.entries(dependencyParams)
        .filter(([, entryValue]) => entryValue !== undefined && entryValue !== null && `${entryValue}`.trim() !== '')
        .sort(([left], [right]) => left.localeCompare(right)),
    })

    if (labelRequestKeyRef.current === requestKey) {
      return
    }

    labelRequestKeyRef.current = requestKey

    let isCancelled = false

    const resolveLabel = async () => {
      try {
        const response = await getFieldDatasourceOptions(
          field.fieldId,
          { tenantId, tenant: tenantId, ...dependencyParams },
          selectedValue,
          1,
          20,
          [selectedValue],
        )
        if (isCancelled) {
          return
        }
        const matchedOption = (response.items ?? []).find(
          (item) => (item.value ?? '').trim().toLowerCase() === selectedValue.toLowerCase(),
        )
        if (!matchedOption) {
          return
        }
        const preferredLabel = buildPreferredSelectionLabel(
          field,
          matchedOption,
          response.displayColumns && response.displayColumns.length > 0
            ? response.displayColumns
            : searchState.tableColumns,
        )
        if (!preferredLabel || preferredLabel.trim().length === 0) {
          return
        }
        if (preferredLabel.trim() === currentLabel) {
          return
        }
        onChange({
          value: selectedValue,
          label: preferredLabel,
        })
      } catch {
        labelRequestKeyRef.current = null
        // keep current label
      }
    }

    void resolveLabel()

    return () => {
      isCancelled = true
    }
  }, [dependencyMissing, dependencyParams, field, onChange, searchState.tableColumns, tenantId, value?.label, value?.value])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <LabelWithRequired label={field.label} required={field.required} />
        {value ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-red-500 hover:underline"
            disabled={disabled}
          >
            Clear
          </button>
        ) : null}
      </div>

      <Dialog open={searchState.isOpen} onOpenChange={(open) => !disabled && searchState.setIsOpen(open)}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between border-gray-200 !bg-background hover:bg-gray-100 disabled:opacity-100"
            disabled={disabled}
          >
            <span
              className={cn(
                "flex items-center gap-2",
                value
                  ? "text-sky-700"
                  : "text-sky-700",
              )}
            >
              <User className={cn("h-4 w-4", value ? "text-sky-700" : "text-gray-400")} />
              {value ? value.label : `Select ${field.label || 'value'}`}
            </span>
            <Search className={cn("h-4 w-4", value ? "text-sky-700" : "text-gray-400")} />
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              {field.label || 'Select data'}
            </DialogTitle>
          </DialogHeader>

          {dependencyMissing ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-sm text-gray-500">
              <XCircle className="mb-4 h-12 w-12 text-gray-300" />
              <p>Please select {dependencyLabel || 'required data'} first.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  placeholder={searchState.placeholder}
                  value={searchState.searchTerm}
                  onChange={(event) => searchState.setSearchTerm(event.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                />
              </div>

              <div className="flex-1 overflow-y-auto">
                {searchState.isLoading ? (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Loading...
                  </div>
                ) : searchState.error ? (
                  <div className="flex h-full items-center justify-center text-sm text-red-600">
                    {searchState.error}
                  </div>
                ) : searchState.options.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    No data found
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="max-h-[40vh] overflow-x-auto overflow-y-auto">
                      <table className="min-w-full text-sm text-gray-700">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                          <tr>
                            {searchState.tableColumns.map((column) => {
                              const columnKey = column.key ?? column.field
                              return (
                                <th key={`${columnKey}-${column.field}`} className="px-4 py-3 text-left font-medium">
                                  {column.label}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {searchState.options.map((option) => {
                            const isSelected = value?.value === option.value
                            return (
                              <tr
                                key={option.value}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleSelect(option)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    handleSelect(option)
                                  }
                                }}
                                className={`cursor-pointer transition-colors ${
                                  isSelected ? 'bg-primary/10' : 'hover:bg-gray-50'
                                }`}
                              >
                                {searchState.tableColumns.map((column) => (
                                  <td key={`${option.value}-${column.key ?? column.field}`} className="px-4 py-2">
                                    {resolveDatasourceCellValue(field, option, column) || '-'}
                                  </td>
                                ))}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <PaginationFooter
                startIndex={searchState.startIndex}
                endIndex={searchState.endIndex}
                totalItems={searchState.totalItems}
                page={searchState.page}
                totalPages={searchState.totalPages}
                canGoPrevious={searchState.canGoPrevious}
                canGoNext={searchState.canGoNext}
                isLoading={searchState.isLoading}
                onPrevious={() => searchState.setPage(Math.max(searchState.page - 1, 1))}
                onNext={() => searchState.setPage(searchState.canGoNext ? searchState.page + 1 : searchState.page)}
              />

              {value ? (
                <div className="border-t pt-3 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Selected: <span className="font-medium text-gray-700">{value.label}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleClear} className="text-red-500">
                    Clear
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function SearchMultiSelectField({
  field,
  value,
  onChange,
  tenantId,
  dependencyParams = {},
  dependencyMissing = false,
  dependencyLabel,
  disabled = false,
}: SearchMultiSelectFieldProps) {
  const searchState = useDatasourceSearch({
    field,
    tenantId,
    dependencyParams,
    dependencyMissing,
    selectedValues: value.map((item) => item.value),
    enabled: !disabled,
    freezeDependencyWhileOpen: true,
  })

  const selectedMap = useMemo(() => {
    const map = new Map<string, SearchSelectValue>()
    value.forEach((item) => {
      const key = item.value?.trim().toLowerCase()
      if (key) {
        map.set(key, item)
      }
    })
    return map
  }, [value])

  const selectedOptionMap = useMemo(() => {
    const map = new Map<string, TemplateFieldDatasourceItem>()
    searchState.options.forEach((option) => {
      const key = option.value?.trim().toLowerCase()
      if (key) {
        map.set(key, option)
      }
    })
    return map
  }, [searchState.options])

  const resolvedSelections = useMemo(() => {
    return value.map((selected) => {
      const key = selected.value?.trim().toLowerCase()
      if (!key) {
        return selected
      }
      const option = selectedOptionMap.get(key)
      if (!option) {
        return selected
      }
      const preferredLabel = buildPreferredSelectionLabel(field, option, searchState.tableColumns)
      return {
        ...selected,
        label: preferredLabel || selected.label || selected.value,
      }
    })
  }, [field, searchState.tableColumns, selectedOptionMap, value])

  const handleToggle = useCallback(
    (item: TemplateFieldDatasourceItem) => {
      const key = item.value.trim().toLowerCase()
      if (!key) {
        return
      }

      if (selectedMap.has(key)) {
        onChange(value.filter((existing) => existing.value.trim().toLowerCase() !== key))
        return
      }

      const preferredLabel = buildPreferredSelectionLabel(field, item, searchState.tableColumns)

      onChange([
        ...value,
        {
          value: item.value,
          label: preferredLabel,
        },
      ])
    },
    [field, onChange, searchState.tableColumns, selectedMap, value],
  )

  const handleClear = useCallback(() => {
    onChange([])
  }, [onChange])

  useEffect(() => {
    if (value.length === 0 || searchState.options.length === 0) {
      return
    }

    let hasChanged = false

    const nextValues = value.map((selected) => {
      const key = selected.value?.trim().toLowerCase()
      if (!key) {
        return selected
      }
      const matchedOption = selectedOptionMap.get(key)
      if (!matchedOption) {
        return selected
      }

      const preferredLabel = buildPreferredSelectionLabel(field, matchedOption, searchState.tableColumns)

      if (!preferredLabel || selected.label === preferredLabel) {
        return selected
      }

      const selectedLabel = selected.label?.trim() ?? ''
      if (selectedLabel === '' || selectedLabel === selected.value || selectedLabel.length < preferredLabel.length) {
        hasChanged = true
        return {
          ...selected,
          label: preferredLabel,
        }
      }

      return selected
    })

    if (hasChanged) {
      onChange(nextValues)
    }
  }, [field, onChange, searchState.options.length, searchState.tableColumns, selectedOptionMap, value])

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <LabelWithRequired label={field.label} required={field.required} />
        {value.length > 0 ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-red-500 hover:underline"
            disabled={disabled}
          >
            Clear
          </button>
        ) : null}
      </div>

      <Dialog open={searchState.isOpen} onOpenChange={(open) => !disabled && searchState.setIsOpen(open)}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="h-auto min-h-10 w-full justify-between border-gray-200 !bg-background hover:bg-gray-100 disabled:opacity-100"
            disabled={disabled}
          >
            <span
              className={cn(
                "flex min-w-0 flex-wrap items-center gap-2",
                resolvedSelections.length > 0
                  ? "text-sky-700"
                  : "text-sky-700",
              )}
            >
              <User
                className={cn(
                  "h-4 w-4 shrink-0",
                  resolvedSelections.length > 0 ? "text-sky-700" : "text-gray-400",
                )}
              />
              {resolvedSelections.length > 0 ? (
                <>
                  {resolvedSelections.slice(0, 3).map((item) => (
                    <span
                      key={item.value}
                      className={cn(
                        "rounded-full bg-white px-2 py-0.5 text-xs shadow",
                        "text-sky-700",
                      )}
                    >
                      {item.label}
                    </span>
                  ))}
                  {resolvedSelections.length > 3 ? (
                    <span className="text-xs text-sky-700">
                      +{resolvedSelections.length - 3} more
                    </span>
                  ) : null}
                </>
              ) : (
                `Select ${field.label || 'value'}`
              )}
            </span>
            <Search
              className={cn(
                "h-4 w-4 shrink-0",
                resolvedSelections.length > 0 ? "text-sky-700" : "text-gray-400",
              )}
            />
          </Button>
        </DialogTrigger>

        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              {field.label || 'Select data'}
            </DialogTitle>
          </DialogHeader>

          {dependencyMissing ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-sm text-gray-500">
              <XCircle className="mb-4 h-12 w-12 text-gray-300" />
              <p>Please select {dependencyLabel || 'required data'} first.</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                <Input
                  placeholder={searchState.placeholder}
                  value={searchState.searchTerm}
                  onChange={(event) => searchState.setSearchTerm(event.target.value)}
                  className="pl-10 bg-gray-50 border-gray-200"
                />
              </div>

              <div className="flex-1 overflow-y-auto">
                {searchState.isLoading ? (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Loading...
                  </div>
                ) : searchState.error ? (
                  <div className="flex h-full items-center justify-center text-sm text-red-600">
                    {searchState.error}
                  </div>
                ) : searchState.options.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-gray-500">
                    No data found
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <div className="max-h-[40vh] overflow-x-auto overflow-y-auto">
                      <table className="min-w-full text-sm text-gray-700">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                          <tr>
                            <th className="w-12 px-4 py-3 text-left font-medium">Select</th>
                            {searchState.tableColumns.map((column) => {
                              const columnKey = column.key ?? column.field
                              return (
                                <th key={`${columnKey}-${column.field}`} className="px-4 py-3 text-left font-medium">
                                  {column.label}
                                </th>
                              )
                            })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {searchState.options.map((option) => {
                            const isSelected = selectedMap.has(option.value.trim().toLowerCase())
                            return (
                              <tr
                                key={option.value}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleToggle(option)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault()
                                    handleToggle(option)
                                  }
                                }}
                                className={`cursor-pointer transition-colors ${
                                  isSelected ? 'bg-primary/10' : 'hover:bg-gray-50'
                                }`}
                              >
                                <td className="px-4 py-2">
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => handleToggle(option)}
                                    className="border-gray-300"
                                  />
                                </td>
                                {searchState.tableColumns.map((column) => (
                                  <td key={`${option.value}-${column.key ?? column.field}`} className="px-4 py-2">
                                    {resolveDatasourceCellValue(field, option, column) || '-'}
                                  </td>
                                ))}
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <PaginationFooter
                startIndex={searchState.startIndex}
                endIndex={searchState.endIndex}
                totalItems={searchState.totalItems}
                page={searchState.page}
                totalPages={searchState.totalPages}
                canGoPrevious={searchState.canGoPrevious}
                canGoNext={searchState.canGoNext}
                isLoading={searchState.isLoading}
                onPrevious={() => searchState.setPage(Math.max(searchState.page - 1, 1))}
                onNext={() => searchState.setPage(searchState.canGoNext ? searchState.page + 1 : searchState.page)}
              />

              {resolvedSelections.length > 0 ? (
                <div className="border-t pt-3 flex items-start justify-between gap-3">
                  <div className="text-sm text-gray-500">
                    Selected:{' '}
                    <span className="font-medium text-gray-700">
                      {resolvedSelections.map((item) => item.label).join(', ')}
                    </span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleClear} className="text-red-500">
                    Clear
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface PaginationFooterProps {
  startIndex: number
  endIndex: number
  totalItems: number
  page: number
  totalPages: number
  canGoPrevious: boolean
  canGoNext: boolean
  isLoading: boolean
  onPrevious: () => void
  onNext: () => void
}

function PaginationFooter({
  startIndex,
  endIndex,
  totalItems,
  page,
  totalPages,
  canGoPrevious,
  canGoNext,
  isLoading,
  onPrevious,
  onNext,
}: PaginationFooterProps) {
  return (
    <div className="border-t pt-3 flex items-center justify-between text-sm text-gray-500">
      <div>
        {totalItems > 0
          ? `Showing ${startIndex}-${endIndex} of ${totalItems} items`
          : 'No data found'}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPrevious}
          disabled={!canGoPrevious || isLoading}
        >
          Previous
        </Button>
        <span>
          Page {page} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={!canGoNext || isLoading}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

interface LabelWithRequiredProps {
  label: string
  required?: boolean
}

function LabelWithRequired({ label, required }: LabelWithRequiredProps) {
  return (
    <div className="flex items-center gap-1 text-sm text-gray-600">
      <span>{label}</span>
      {required ? <span className="text-red-500">*</span> : null}
    </div>
  )
}
